// SP-1 — this material's printed tags, in the shell's right-hand dock: what
// each tag says, where it has got to, and a way to print another.
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import DockPanel from '@/components/shell/DockPanel';
import { MaterialTagForm } from '@/components/MaterialTagForm';
import { printPdf } from '@/utils/printPdf';
import '@/styles/clip-tags.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATE_WORDS = { tagged: 'printed', on_site: 'on site', installed: 'installed' };
const when = (iso) => (iso ? new Date(iso).toLocaleDateString() : 'not scanned');
const day = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US',
  { month: 'short', day: 'numeric' }) : '');

export const ClipTagsPanel = ({ clip, onClose, onUpdate }) => {
  const [tags, setTags] = useState([]);
  // PR-1c — where these cartons are standing now, off the clip's receipt.
  const [nowAt, setNowAt] = useState(null);
  const [printing, setPrinting] = useState(false);
  // SP-2 — the tag whose face is being corrected and reprinted.
  const [fixing, setFixing] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/clips/${clip.id}/tags`)
      .then((r) => {
        setTags(r.data?.tags || []);
        setNowAt(r.data?.now_at_where
          ? { where: r.data.now_at_where, location: r.data.now_at_location,
              since: r.data.now_at_since }
          : null);
      })
      .catch(() => { setTags([]); setNowAt(null); });
  }, [clip.id]);

  useEffect(() => { load(); }, [load]);

  return (
    <DockPanel onClose={onClose} width="420px">
      <aside className="kct" data-testid="clip-tags-panel">
        <div className="kp-head">
          <div>
            <div className="k">Material · tags</div>
            <div className="t">{clip.title || clip.name}</div>
          </div>
          <button type="button" className="kct-btn" onClick={onClose}
            data-testid="clip-tags-close">Close</button>
        </div>

        {nowAt ? (
          <p className="kct-none" data-testid="clip-tags-now-at">
            {`Now at: ${[nowAt.where, nowAt.location].filter(Boolean).join(' · ')}`
              + `${nowAt.since ? ` · since ${day(nowAt.since)}` : ''}`}
          </p>
        ) : null}

        <div className="kct-list" data-testid="clip-tags-list">
          {tags.map((tag) => (
            <div className="kct-row" key={tag.id} data-testid={`clip-tag-row-${tag.id}`}>
              {/* SP-2 — a box says what it carries and which box it is. */}
              <span className="no">
                {tag.code} · {tag.package_count
                  ? `${tag.package_count} of ${tag.count_total} · box ${tag.package_index}`
                  : `${tag.count_index} of ${tag.count_total}`}
              </span>
              <span className="w">{[tag.room, tag.location].filter(Boolean).join(' · ')}</span>
              <span className="m">
                {STATE_WORDS[tag.state] || 'printed'} · last scan {when(tag.last_scan_at)}
                {tag.at_where
                  && (!nowAt || tag.at_where !== nowAt.where
                    || (tag.at_location || '') !== (nowAt.location || ''))
                  ? ` · (printed: ${[tag.at_where, tag.at_location].filter(Boolean).join(' · ')})`
                  : ''}
              </span>
              {tag.superseded_by ? (
                <span className="sup" data-testid={`tag-superseded-${tag.id}`}>
                  replaced by a newer label
                </span>
              ) : (
                <span className="kct-acts">
                  <button type="button" className="kct-btn"
                    onClick={() => printPdf(
                      `${API}/projects/${clip.project_id}/material-tags/print.pdf`,
                      { params: { ids: tag.id, stock: tag.stock || 'ql62' } })}
                    data-testid={`tag-reprint-${tag.id}`}>Reprint</button>
                  <button type="button" className="kct-btn"
                    onClick={() => setFixing(tag)}
                    data-testid={`tag-edit-${tag.id}`}>Edit &amp; reprint</button>
                </span>
              )}
            </div>
          ))}
          {!tags.length ? (
            <p className="kct-none" data-testid="clip-tags-none">No tags on this material yet.</p>
          ) : null}
        </div>

        <div className="kct-acts">
          <button type="button" className="kct-btn kct-btn--brand"
            onClick={() => setPrinting(true)} data-testid="clip-tags-print-another">
            Print another
          </button>
        </div>

        {fixing && clip.project_id ? (
          <MaterialTagForm
            projectId={clip.project_id}
            source="clip"
            body={{ clip_id: clip.id }}
            existingTag={fixing}
            replaceTag={fixing}
            title={fixing.title || clip.title || ''}
            room={fixing.room || ''}
            location={fixing.location || ''}
            tradePrefix={clip.spec_category || ''}
            trade={fixing.trade || ''}
            tagName={fixing.tag_name || ''}
            tagNote={fixing.instructions || ''}
            imageCount={(clip.images || []).length}
            onMade={() => { load(); onUpdate?.(); }}
            onClose={() => { setFixing(null); load(); }}
          />
        ) : null}

        {printing && clip.project_id ? (
          <MaterialTagForm
            projectId={clip.project_id}
            source="clip"
            body={{ clip_id: clip.id }}
            title={clip.title || clip.name || ''}
            room={clip.room || ''}
            roomId={clip.room_id || null}
            location={clip.room_location || ''}
            tradePrefix={clip.spec_category || ''}
            trade={clip.last_tag_trade || ''}
            tagName={clip.tag_name || ''}
            tagNote={clip.tag_note || ''}
            imageCount={(clip.images || []).length}
            onMade={() => { load(); onUpdate?.(); }}
            onClose={() => setPrinting(false)}
          />
        ) : null}
      </aside>
    </DockPanel>
  );
};

export default ClipTagsPanel;
